import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useStandort } from '../../../components/layouts/use-standort';
import { usePermissions } from '../../../components/rbac/use-permission';
import { P } from '../../../lib/rbac/permissions.generated';
import {
  centsAusEuro,
  euroAusCents,
  standortAngebotApi,
  type AngebotForm,
  type AngebotPaket,
  type StandortAngebot,
} from '../../../lib/api/standortAngebot';

const errorText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

/**
 * Angebot am Standort (Entscheidung F08, 2026-09-21). Unterrichtsformen und Pakete
 * pflegt die globale Administration; hier entscheidet der Standort, was er davon
 * anbietet und zu welchem Preis. Leere Felder bedeuten „wie global".
 */
const StandortAngebotPage: React.FC = () => {
  const { selectedStandortId, selectedStandort } = useStandort();
  const { can } = usePermissions();
  const [angebot, setAngebot] = useState<StandortAngebot | null>(null);
  const [loading, setLoading] = useState(true);
  const [speichert, setSpeichert] = useState<string | null>(null);

  const darfFormen =
    selectedStandortId != null &&
    can(P.standort.unterrichtsformen_und_pakete.unterrichtsformen_bearbeiten, {
      standortId: selectedStandortId,
    });
  const darfPakete =
    selectedStandortId != null &&
    can(P.standort.unterrichtsformen_und_pakete.pakete_bearbeiten, { standortId: selectedStandortId });

  const load = useCallback(async () => {
    if (!selectedStandortId) return;
    setLoading(true);
    try {
      setAngebot(await standortAngebotApi.get(selectedStandortId));
    } catch (e) {
      toast.error(errorText(e, 'Angebot konnte nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  }, [selectedStandortId]);

  useEffect(() => {
    void load();
  }, [load]);

  const speichereForm = async (form: AngebotForm, werte: Partial<AngebotForm['abweichung']>) => {
    if (!selectedStandortId) return;
    setSpeichert(`form-${form.id}`);
    try {
      setAngebot(
        await standortAngebotApi.setForm(selectedStandortId, form.id, { ...form.abweichung, ...werte }),
      );
    } catch (e) {
      toast.error(errorText(e, 'Speichern fehlgeschlagen'));
      await load();
    } finally {
      setSpeichert(null);
    }
  };

  const speicherePaket = async (paket: AngebotPaket, werte: Partial<AngebotPaket['abweichung']>) => {
    if (!selectedStandortId) return;
    setSpeichert(`paket-${paket.package_id}`);
    try {
      setAngebot(
        await standortAngebotApi.setPaket(selectedStandortId, paket.package_id, {
          ...paket.abweichung,
          ...werte,
        }),
      );
    } catch (e) {
      toast.error(errorText(e, 'Speichern fehlgeschlagen'));
      await load();
    } finally {
      setSpeichert(null);
    }
  };

  if (!selectedStandortId) {
    return (
      <div className="mx-auto max-w-5xl p-4 sm:p-6">
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Bitte oben einen Standort auswählen.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Angebot am Standort</h1>
        <p className="text-muted-foreground">
          {selectedStandort?.name ?? 'Dieser Standort'} nutzt das gemeinsame Angebot. Nur was Sie hier
          ändern, gilt abweichend — leere Felder bedeuten „wie überall".
        </p>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Lade Angebot…</p>}
      {!loading && angebot?.unterrichtsformen.length === 0 && (
        <p className="text-sm text-muted-foreground">Es sind noch keine Unterrichtsformen angelegt.</p>
      )}

      {(angebot?.unterrichtsformen ?? []).map((form) => {
        const abweichend = form.abweichung.aktiv !== null || form.abweichung.aufnahmegebuehr_cents !== null;
        return (
          <Card key={form.id}>
            <CardHeader className="gap-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="flex items-center gap-2">
                  {form.name}
                  {!form.wirksam.aktiv && <Badge size="sm" variant="secondary">hier nicht im Angebot</Badge>}
                  {abweichend && <Badge size="sm" variant="info">abweichend</Badge>}
                </CardTitle>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch
                      checked={form.wirksam.aktiv ?? false}
                      disabled={!darfFormen || speichert === `form-${form.id}`}
                      onCheckedChange={(an: boolean) =>
                        speichereForm(form, { aktiv: an === form.global.aktiv ? null : an })
                      }
                    />
                    Am Standort anbieten
                  </label>
                  {abweichend && darfFormen && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => speichereForm(form, { aktiv: null, aufnahmegebuehr_cents: null })}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" /> Wie überall
                    </Button>
                  )}
                </div>
              </div>
              <CardDescription>
                {form.dauer_minutes ? `${form.dauer_minutes} Minuten` : 'Dauer nicht gesetzt'}
                {form.max_schueler ? ` · bis ${form.max_schueler} Teilnehmende` : ''} · Aufnahmegebühr
                überall: {euroAusCents(form.global.aufnahmegebuehr_cents) || '—'} €
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-xs text-muted-foreground" htmlFor={`gebuehr-${form.id}`}>
                    Aufnahmegebühr hier (€)
                  </label>
                  <Input
                    id={`gebuehr-${form.id}`}
                    uiSize="sm"
                    className="w-32"
                    inputMode="decimal"
                    placeholder={euroAusCents(form.global.aufnahmegebuehr_cents) || 'wie überall'}
                    defaultValue={euroAusCents(form.abweichung.aufnahmegebuehr_cents)}
                    disabled={!darfFormen}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      const cents = centsAusEuro(e.target.value);
                      if (cents === form.abweichung.aufnahmegebuehr_cents) return;
                      void speichereForm(form, { aufnahmegebuehr_cents: cents });
                    }}
                  />
                </div>
              </div>

              {form.pakete.length > 0 && (
                <div className="rounded-md border">
                  <Table density="compact">
                    <TableHeader>
                      <TableRow>
                        <TableHead density="compact">Paket</TableHead>
                        <TableHead density="compact">Einheiten</TableHead>
                        <TableHead density="compact">Preis überall</TableHead>
                        <TableHead density="compact">Preis hier (€)</TableHead>
                        <TableHead density="compact">Anbieten</TableHead>
                        <TableHead density="compact" className="text-right" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.pakete.map((paket) => {
                        const paketAbweichend =
                          paket.abweichung.aktiv !== null ||
                          paket.abweichung.grundpreis_cents !== null ||
                          paket.abweichung.sofortpreis_cents !== null ||
                          paket.abweichung.reihenfolge !== null;
                        return (
                          <TableRow key={paket.package_id}>
                            <TableCell density="compact" className="font-medium">
                              {paket.name}
                              {paketAbweichend && (
                                <Badge size="sm" variant="info" className="ml-2">abweichend</Badge>
                              )}
                            </TableCell>
                            <TableCell density="compact">{paket.einheiten}</TableCell>
                            <TableCell density="compact">
                              {euroAusCents(paket.global.grundpreis_cents) || '—'} €
                            </TableCell>
                            <TableCell density="compact">
                              <Input
                                uiSize="sm"
                                className="w-28"
                                inputMode="decimal"
                                placeholder={euroAusCents(paket.global.grundpreis_cents) || 'wie überall'}
                                defaultValue={euroAusCents(paket.abweichung.grundpreis_cents)}
                                disabled={!darfPakete}
                                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                  const cents = centsAusEuro(e.target.value);
                                  if (cents === paket.abweichung.grundpreis_cents) return;
                                  void speicherePaket(paket, { grundpreis_cents: cents });
                                }}
                              />
                            </TableCell>
                            <TableCell density="compact">
                              <Switch
                                checked={paket.wirksam.aktiv ?? false}
                                disabled={!darfPakete || speichert === `paket-${paket.package_id}`}
                                onCheckedChange={(an: boolean) =>
                                  speicherePaket(paket, {
                                    aktiv: an === paket.global.aktiv ? null : an,
                                  })
                                }
                              />
                            </TableCell>
                            <TableCell density="compact" className="text-right">
                              {paketAbweichend && darfPakete && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    speicherePaket(paket, {
                                      aktiv: null,
                                      grundpreis_cents: null,
                                      sofortpreis_cents: null,
                                      reihenfolge: null,
                                    })
                                  }
                                >
                                  <RotateCcw className="mr-2 h-4 w-4" /> Wie überall
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default StandortAngebotPage;
